import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { initializeEditor } from 'src/app/utils/editorConfig';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { requireMinOneColleagueValidator } from 'src/app/validators/at-least-one-colleague';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { environment } from 'src/environments/environment';
import { NotificationService } from 'src/app/services/notification.service';
import { ToastrService } from 'ngx-toastr';
import { ICerere } from '../../cereri.interface';
import { IColeg, RequestMessageInternPayload, RequestMessageNotitaPayload, RequestMessageToCetateanPayload } from './mesagerie.interface';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, map, startWith, Subscription } from 'rxjs';
import { CereriService } from '../../cereri.service';
import { helper } from 'src/environments/helper';
import { UsersService } from 'src/app/services/users.service';
import { MatDialog } from '@angular/material/dialog';
import { CerereDetaliiSabloaneComponent } from '../sabloane/cerere-detalii-sabloane.component';
import { DialogService } from 'src/app/services/dialog.service';

@Component({
    selector: 'app-mesagerie',
    templateUrl: './mesagerie.component.html',
    styleUrls: ['./mesagerie.component.scss']
})
export class MesagerieComponent implements OnInit {
    @Input() cerere!: ICerere;
    @Output() cerereChanged: EventEmitter<ICerere> = new EventEmitter<ICerere>();

    isLoading = false;
    helper = helper;

    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;

    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;

    uploading: any = {
        files_mesagerie_cetatean: false,
        files_mesagerie_intern: false,
        files_mesagerie_notita: false,
    };

    files: any = {
        files_mesagerie_cetatean: [],
        files_mesagerie_intern: [],
        files_mesagerie_notita: [],
    }

    // mesagerie
    selectedMessagingToggle = 'internalMsg';

    sendMessageCetateanForm!: FormGroup;
    sendMessageInternForm!: FormGroup;
    sendMessageNotitaForm!: FormGroup;
    notifyAnswer!: FormGroup;

    editorConfig!: AngularEditorConfig;

    uploadedFileNames: string[] = [];

    responseTimes = [
        { key: '3', value: 3 },
        { key: '5', value: 5 },
        { key: '7', value: 7 },
        { key: '10', value: 10 },
    ];

    // coleagues functionality
    separatorKeysCodes: number[] = [ENTER, COMMA];
    filteredColeagues!: any;
    coleagues: IColeg[] = [];
    allColeagues: IColeg[] = [];
    coleaguesCtrl = new FormControl('');
    @ViewChild('coleagueInput') coleagueInput!: ElementRef<HTMLInputElement>;

    // Subscription to track our sabloane added from the button below messaging component
    private contentSubscription!: Subscription;

    constructor(
        private fb: FormBuilder,
        private notificationService: NotificationService,
        private cereriService: CereriService,
        private userService: UsersService,
        private dialog: MatDialog,
        private dialogService: DialogService,
        public toastr: ToastrService,
    ) { }

    ngOnInit(): void {
        this.editorConfig = initializeEditor();
        this.getUsersActive();
        this.subscribeToContent();

        this.sendMessageCetateanForm = this.fb.group({
            message: new FormControl('', [Validators.required]),
            type: new FormControl('1'),
            files: this.fb.array([]),
            notify: new FormControl(false),
        });

        this.sendMessageInternForm = this.fb.group({
            message: new FormControl('', [Validators.required]),
            type: new FormControl(2),
            coleagues: new FormControl(this.coleagues, requireMinOneColleagueValidator()),
            files: this.fb.array([]),
            notify: new FormControl(false),
        })

        this.sendMessageNotitaForm = this.fb.group({
            message: new FormControl('', [Validators.required]),
            type: new FormControl(3),
            files: this.fb.array([]),
            notify: new FormControl(false),
        })

        this.notifyAnswer = new FormGroup({
            notify_date_expire: new FormControl('', [Validators.required]),
        });

        this.filteredColeagues = this.coleaguesCtrl.valueChanges.pipe(
            startWith(null),
            map((value: string | null) =>
                value ? this._coleaguesFilter(value) : this.allColeagues.slice()
            )
        );
    }

    ngOnDestroy(): void {
        if (this.contentSubscription) {
            this.contentSubscription.unsubscribe();
        }
    }

    onMessagingChange(event: MatButtonToggleChange) {
        this.selectedMessagingToggle = event.value;
    }

    // coleagues section
    private _coleaguesFilter(value: string | IColeg): IColeg[] {
        let filterValue = '';
        if (typeof value === 'string') {
            filterValue = value.toLowerCase();
        } else {
            filterValue = value.name.toLowerCase();
        }

        return this.allColeagues.filter((coleague) =>
            coleague.name.toLowerCase().includes(filterValue)
        );
    }

    addColeague(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();

        if (value) {
            const foundOption = this.allColeagues.find(
                (coleague) => coleague.name.toLowerCase() === value.toLowerCase()
            );
            if (foundOption) {
                this.coleagues.push(foundOption);
                this.sendMessageInternForm.controls['coleagues'].setValue([...this.coleagues]);
                this.sendMessageInternForm.controls['coleagues'].updateValueAndValidity();
            }
        }

        event.chipInput!.clear();
        this.coleaguesCtrl.setValue(null);
    }

    removeColeague(coleague: IColeg): void {
        const index = this.coleagues.indexOf(coleague);

        if (index >= 0) {
            this.coleagues.splice(index, 1);
            // Update the form control to reflect changes and re-validate
            this.sendMessageInternForm.controls['coleagues'].setValue([...this.coleagues]);
            this.sendMessageInternForm.controls['coleagues'].updateValueAndValidity();
        }
    }

    selectedColeagues(event: MatAutocompleteSelectedEvent): void {
        const selectedOption = this.allColeagues.find(
            (coleague) => coleague.name === event.option.viewValue
        );
        if (
            selectedOption &&
            !this.coleagues.find(
                (coleague) => coleague.id_user === selectedOption.id_user
            )
        ) {
            this.coleagues.push(selectedOption);
            this.coleagueInput.nativeElement.value = '';
            this.coleaguesCtrl.setValue(null);
            // Update the form control to reflect changes and re-validate
            this.sendMessageInternForm.controls['coleagues'].setValue([...this.coleagues]);
            this.sendMessageInternForm.controls['coleagues'].updateValueAndValidity();
        }
    }

    getUsersActive() {
        this.userService
            .getUsersActive()
            .pipe(map((response: any) => response.data))
            .subscribe((requestData) => {
                this.allColeagues = requestData;
            });
    }

    // files section
    addFileCetatean(event: any, key: 'file') {
        event.eventPreventDefault = true;
        const inputElement = event.target as HTMLInputElement;
        if (!inputElement.files) {
            return;
        }

        const files = inputElement.files;

        // Iterate through the selected files and append them to the 'files' array in the form
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            this.uploading[key] = true;

            const formData = new FormData();
            formData.append('file', file, file.name);

            this.cereriService.fileUpload(formData).subscribe({
                next: (result: any) => {
                    let response =
                        typeof result.status_code !== 'undefined' ? result : result.error;
                    if (typeof response.status_code !== 'undefined') {
                        if (
                            response.status_code == 200 &&
                            typeof response.data !== 'undefined'
                        ) {
                            // Handle the uploaded file by adding it to the 'files' array in the form
                            const files = this.sendMessageCetateanForm.get('files') as FormArray;
                            files.push(new FormControl(result.data));

                            this.uploading[key] = false;
                            this.openToast({ title: this.successTitle, message: helper.dialogConfig.generalMessages.fileSuccessfullyUploaded, type: this.successIcon, });
                        }
                    } else {
                        let errorMessage = environment.config.customNotifications.generalMessages.error;
                        this.notificationService.warningSwal(this.errorTitle, errorMessage, this.errorIcon);
                        this.isLoading = false;
                        this.uploading[key] = false;
                    }
                },
                error: (result: any) => {
                    this.isLoading = false;
                    this.uploading[key] = false;
                    this.onHttpError(result.error);
                },
            });
        }
    }

    addFileIntern(event: any, key: 'file') {
        event.eventPreventDefault = true;
        const inputElement = event.target as HTMLInputElement;
        if (!inputElement.files) {
            return;
        }

        const files = inputElement.files;

        // Iterate through the selected files and append them to the 'files' array in the form
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            this.uploading[key] = true;

            const formData = new FormData();
            formData.append('file', file, file.name);

            this.cereriService.fileUpload(formData).subscribe({
                next: (result: any) => {
                    let response =
                        typeof result.status_code !== 'undefined' ? result : result.error;
                    if (typeof response.status_code !== 'undefined') {
                        if (response.status_code == 200 && typeof response.data !== 'undefined') {
                            // Handle the uploaded file by adding it to the 'files' array in the form
                            const files = this.sendMessageInternForm.get('files') as FormArray;
                            files.push(new FormControl(result.data));

                            this.uploading[key] = false;

                            this.openToast({ title: this.successTitle, message: helper.dialogConfig.generalMessages.fileSuccessfullyUploaded, type: this.successIcon });
                        }
                    } else {
                        let errorMessage = environment.config.customNotifications.generalMessages.error;
                        this.notificationService.warningSwal(this.errorTitle, errorMessage, this.errorIcon);
                        this.isLoading = false;
                        this.uploading[key] = false;
                    }
                },
                error: (result: any) => {
                    this.isLoading = false;
                    this.uploading[key] = false;
                    this.onHttpError(result.error);
                },
            });
        }
    }

    addFileNotita(event: any, key: 'file') {
        event.eventPreventDefault = true;
        const inputElement = event.target as HTMLInputElement;
        if (!inputElement.files) {
            return;
        }

        const files = inputElement.files;

        // Iterate through the selected files and append them to the 'files' array in the form
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            this.uploading[key] = true;

            const formData = new FormData();
            formData.append('file', file, file.name);

            this.cereriService.fileUpload(formData).subscribe({
                next: (result: any) => {
                    let response =
                        typeof result.status_code !== 'undefined' ? result : result.error;
                    if (typeof response.status_code !== 'undefined') {
                        if (response.status_code == 200 && typeof response.data !== 'undefined') {
                            // Handle the uploaded file by adding it to the 'files' array in the form
                            const files = this.sendMessageNotitaForm.get('files') as FormArray;
                            files.push(new FormControl(result.data));

                            this.uploading[key] = false;

                            this.openToast({ title: this.successTitle, message: helper.dialogConfig.generalMessages.fileSuccessfullyUploaded, type: this.successIcon });
                        }
                    } else {
                        let errorMessage = environment.config.customNotifications.generalMessages.error;
                        this.notificationService.warningSwal(this.errorTitle, errorMessage, this.errorIcon);
                        this.isLoading = false;
                        this.uploading[key] = false;
                    }
                },
                error: (result: any) => {
                    this.isLoading = false;
                    this.uploading[key] = false;
                    this.onHttpError(result.error);
                },
            });
        }
    }

    onHttpError(res: any, showErrorsInToast: boolean = false) {
        if (res.errors && res.errors.message && res.errors.message.length > 0) {
            res.errors.message.forEach((msg: string) => {
                if (showErrorsInToast) {
                    this.openToast({ title: this.errorTitle, message: msg, type: this.errorType, });
                } else {
                    this.notificationService.warningSwal(this.errorTitle, msg, this.errorIcon);
                }
            });
        } else {
            let errorMessage = environment.config.customNotifications.generalMessages.error;
            this.notificationService.warningSwal(this.errorTitle, errorMessage, this.errorIcon);
        }
    }

    removeFileCetatean(index: number) {
        // const currentFiles = this.sendMessageCetateanForm.value.files || [];
        // const currentFiles = this.sendMessageCetateanForm.get('files')?.value || [];
        // currentFiles.splice(index, 1);
        // this.sendMessageCetateanForm.patchValue({ files: currentFiles });

        const files = this.sendMessageCetateanForm.get('files') as FormArray;
        if (index > -1 && index < files.length) {
            files.removeAt(index);
        }
    }

    removeFileIntern(index: number) {
        const files = this.sendMessageInternForm.get('files') as FormArray;
        if (index > -1 && index < files.length) {
            files.removeAt(index);
        }
    }

    removeFileNota(index: number) {
        const files = this.sendMessageNotitaForm.get('files') as FormArray;
        if (index > -1 && index < files.length) {
            files.removeAt(index);
        }
    }

    openToast(object: any) {
        const { type, message, title } = object;

        let options = this.toastr.toastrConfig;
        options.progressBar = true;
        options.preventDuplicates = true;
        options.closeButton = true;

        const inserted = this.toastr.show(
            message,
            title,
            options,
            options.iconClasses[type]
        );
    }

    onFileSelected(event: any, section: any) {
        const files = event.target.files;
        if (files) {
            var filesArray;
            switch (section) {
                case 'cetatean':
                    filesArray = this.sendMessageCetateanForm.get('files') as FormArray;
                    break;

                case 'intern':
                    filesArray = this.sendMessageInternForm.get('files') as FormArray;
                    break;

                case 'nota':
                    filesArray = this.sendMessageNotitaForm.get('files') as FormArray;
                    break;
            }

            Array.from(files).forEach((file: any) => {
                if (!this.uploadedFileNames.includes(file.name)) {
                    this.uploadedFileNames.push(file.name);
                    const fileControl = new FormControl(file);

                    filesArray.push(fileControl);
                }
            });
        }
    }

    resetAllForms() {
        if (this.sendMessageCetateanForm.value) {
            this.sendMessageCetateanForm.get('message')?.setValue('');
            this.sendMessageCetateanForm.get('type')?.setValue('1');
            this.sendMessageCetateanForm.get('notify')?.setValue(false);
            this.notifyAnswer.get('notify_date_expire')?.setValue(null);

            const filesFormArray = this.sendMessageCetateanForm.get(
                'files'
            ) as FormArray;
            filesFormArray.clear();
            this.sendMessageCetateanForm.clearValidators();
        }

        if (this.sendMessageInternForm.value) {
            this.coleagues = [];
            this.sendMessageInternForm.get('message')?.setValue(null);
            this.sendMessageInternForm.get('type')?.setValue('2');
            this.sendMessageInternForm.get('notify')?.setValue(false);

            const filesFormArray = this.sendMessageInternForm.get(
                'files'
            ) as FormArray;
            filesFormArray.clear();
            this.sendMessageInternForm.clearValidators();
        }

        if (this.sendMessageNotitaForm.value) {
            this.sendMessageNotitaForm.get('message')?.setValue('');
            this.sendMessageNotitaForm.get('type')?.setValue('3');
            this.sendMessageNotitaForm.get('notify')?.setValue(false);

            const filesFormArray = this.sendMessageNotitaForm.get(
                'files'
            ) as FormArray;
            filesFormArray.clear();
            this.sendMessageNotitaForm.clearValidators();
        }
    }

    submitMessage(section: any) {
        this.isLoading = true;

        var newMessage;
        switch (section) {
            case 'cetatean':
                newMessage = this.sendMessageCetateanForm
                    .value as RequestMessageToCetateanPayload;

                if (this.sendMessageCetateanForm.get('notify')?.value === true) {
                    newMessage = Object.assign(newMessage, {
                        notify_date_expire: this.notifyAnswer.value.notify_date_expire,
                    });
                }
                break;

            case 'intern':
                newMessage = this.sendMessageInternForm
                    .value as RequestMessageInternPayload;
                break;

            case 'nota':
                newMessage = this.sendMessageNotitaForm
                    .value as RequestMessageNotitaPayload;
                break;
        }

        this.cereriService
            .postMessage(this.cerere.id_cerere, newMessage)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                    this.uploadedFileNames = [];
                    this.resetAllForms();
                })
            )
            .subscribe({
                next: (res) => {
                    this.isLoading = true;
                    this.notificationService.warningSwal(
                        this.successTitle,
                        'Mesaj trimis cu succes!',
                        this.successIcon
                    ).then(() => {
                        switch (section) {
                            case 'cetatean':
                                this.sendMessageCetateanForm.patchValue({
                                    message: '',
                                    files: [],
                                    notify: false,
                                });
                                this.notifyAnswer.patchValue({
                                    // notify: false,
                                    notify_date_expire: null,
                                });
                                break;

                            case 'intern':
                                this.sendMessageInternForm.patchValue({
                                    message: '',
                                    files: [],
                                    coleagues: this.coleagues,
                                });
                                break;

                            case 'nota':
                                this.sendMessageNotitaForm.patchValue({
                                    message: '',
                                    files: [],
                                });
                                break;
                        }

                        this.getRequestInfo();
                    });
                },
                error: (err) => {
                    let msg;
                    try {
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    // This subscribes to any content change coming from Sabloane button below
    subscribeToContent() {
        this.contentSubscription = this.cereriService.sablonContent$.subscribe({
            next: (content) => {
                this.updateMessageFormControl(content);
            },
        });
    }
    // This updated our form control
    updateMessageFormControl(content: string) {
        // Update the 'message' form control with the new content
        this.sendMessageCetateanForm.controls['message'].setValue(content);
    }

    openSabloane() {
        const dialogRef = this.dialog.open(CerereDetaliiSabloaneComponent, {
            ...this.dialogService.getDialogConfigMD()
        });

        dialogRef
            .afterClosed()
            .subscribe(({ success }: { success: boolean }) => { });
    }

    getRequestInfo() {
        this.isLoading = true;
        this.cereriService
            .getCerere(this.cerere.id_cerere)
            .pipe(map((response: any) => response.data))
            .subscribe((requestData) => {
                this.isLoading = false;
                this.cerere = requestData;
                this.cerereChanged.emit(this.cerere);
            });
    }
}
